import React from 'react'
import { Box, Button } from '@mui/material'
import { Table } from 'shared/ui/Table/Table'
import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import { GoogleLoginButton, GDriveFile } from 'infrastructure/googleDrive'
import { openNode } from '../../../utility/TreeNavigation'
import { ConfirmCommitButton, ConfirmWithDialogButton } from 'shared/ui'
import { doInBatches } from '../../../../utility'
import { Checkmark } from '../../../../ingestion/IMSpanish/components/Checkmark'
import {
  deleteExisting,
  doUpdate,
  exportFileData,
  useCurrentTree,
  useTreeIdMap,
  useUpdatesItemList,
} from './utility'
import { UpdateItem } from './types'
import {
  currentTreeIdsAtom,
  doingSomethingAtom,
  gDriveFilesAtom,
} from './atoms'

export function UPVideos(ids: { treeId: string; versionId: string }) {
  useSetAtom(currentTreeIdsAtom)(ids)
  return (
    <Box>
      <Box>UP Videos</Box>
      <GoogleLoginButton hideWhenLoggedIn={true} />
      <UpdatesTable />
    </Box>
  )
}

function UpdatesTable() {
  const { updates } = useUpdatesItemList()
  const idMap = useTreeIdMap()

  const columns = [
    {
      title: <strong>File</strong>,
      width: 50,
      rowContent({ file }: UpdateItem) {
        return file.name
      },
    },
    {
      title: <strong>Activity</strong>,
      width: 50,
      rowContent({ activity }: UpdateItem) {
        return activity.title
      },
    },
    {
      title: <strong>Question</strong>,
      width: 50,
      rowContent({ question }: UpdateItem) {
        return question.title
      },
    },
    {
      title: <strong>Complete</strong>,
      width: 50,
      rowContent({ existingNode }: UpdateItem) {
        return existingNode ? <Checkmark /> : ''
      },
    },
    {
      title: <strong>Go</strong>,
      width: 50,
      rowContent({ question }: UpdateItem) {
        return <Button onClick={() => openNode(question, idMap)}>Go</Button>
      },
    },
    {
      title: <strong>Update</strong>,
      width: 50,
      rowContent(update: UpdateItem) {
        return <UpdateOneButton update={update} />
      },
    },
  ]
  return (
    <Box>
      <Table
        columns={columns}
        items={updates}
        toolBarItems={[
          <ExportAllButton key="exportAll" />,
          <DeleteAllButton key="deleteAll" />,
          <UpdateAllButton key="updateAll" />,
        ]}
      />
      <ExtraFilesTable />
    </Box>
  )
}

function UpdateOneButton({ update }: { update: UpdateItem }) {
  const { resetTree } = useCurrentTree()
  const [doing, setDoing] = useAtom(doingSomethingAtom)

  if (update.existingNode) {
    const doDelete = async () => {
      if (!update.existingNode) return
      setDoing(true)
      await deleteExisting(update)
      setTimeout(() => {
        resetTree?.()
      }, 500)
      setDoing(false)
    }
    return (
      <ConfirmCommitButton disabled={doing} onClick={doDelete}>
        Delete
      </ConfirmCommitButton>
    )
  }
  const updateThis = async () => {
    setDoing(true)
    await doUpdate(update)
    setTimeout(() => {
      resetTree?.()
    }, 500)
    setDoing(false)
  }
  return (
    <ConfirmCommitButton disabled={doing} onClick={updateThis}>
      Update
    </ConfirmCommitButton>
  )
}

function ExportAllButton() {
  const files = useAtomValue(gDriveFilesAtom)
  return (
    <Button onClick={() => exportFileData('allFiles', files)}>
      Export All
    </Button>
  )
}

function UpdateAllButton() {
  const { resetTree } = useCurrentTree()
  const { updates } = useUpdatesItemList()
  const [doing, setDoing] = useAtom(doingSomethingAtom)
  const notCompleted = updates.filter(({ existingNode }) => !existingNode)
  const doThisUpdate = async () => {
    setDoing(true)
    await doInBatches(notCompleted, doUpdate)
    setTimeout(() => {
      resetTree?.()
    }, 500)
    setDoing(false)
  }
  return (
    <ConfirmWithDialogButton
      disabled={doing}
      dialogTitle="Update All"
      dialogContent={`Add all ${notCompleted.length} nodes?`}
      onClick={doThisUpdate}
    >
      Update All
    </ConfirmWithDialogButton>
  )
}

function DeleteAllButton() {
  const { resetTree } = useCurrentTree()
  const { updates } = useUpdatesItemList()
  const [doing, setDoing] = useAtom(doingSomethingAtom)
  const withExisting = updates.filter(({ existingNode }) => !!existingNode)
  const doUpdate = async () => {
    setDoing(true)
    await doInBatches(withExisting, deleteExisting)
    setTimeout(() => {
      resetTree?.()
    }, 500)
    setDoing(false)
  }
  return (
    <ConfirmWithDialogButton
      disabled={doing}
      dialogTitle="Delete All"
      dialogContent={`Delete all ${withExisting.length} existing?`}
      onClick={doUpdate}
    >
      Delete All
    </ConfirmWithDialogButton>
  )
}

function ExtraFilesTable() {
  const { extraFiles } = useUpdatesItemList()
  const columns = [
    {
      title: <strong>Name</strong>,
      width: 100,
      rowContent(file: GDriveFile) {
        return file.name
      },
    },
  ]
  const exportContent = () => {
    exportFileData('notImported', extraFiles)
  }
  return (
    <Table
      columns={columns}
      items={extraFiles}
      toolBarItems={[
        <Button key="export" onClick={exportContent}>
          Export
        </Button>,
      ]}
    />
  )
}
